import { Box, Divider, Typography } from '@mui/material';
import { Stack } from '@mui/system';
import { ProtectedIcon } from '@tix-ticketing/ui-icons/filled';
import { FormattedMessage, useIntl } from 'react-intl';
import type { OrderResponse } from 'api/generated/types.gen';
import { CartDiscountCode } from 'components/cart/cart-discount-code';
import { useAppState } from 'lib/app-state';
import { FormattedPrice } from 'lib/helpers/formatted-price';
import { getOrderChargeGroups, getOrderDonationsCount, getOrderDonationsTotal, getOrderGiftCardsCount, getOrderGiftCardsTotal, getOrderMembershipsCount, getOrderMembershipsTotal, getOrderProductsCount, getOrderProductsTotal, getOrderServiceLabel, getOrderTicketsCount, getOrderTicketsTotal, orderTotalTicketSeparateFees } from 'lib/helpers/order-helpers';
import type { ExchangeCart } from 'lib/hooks/use-exchange-cart';
import type { PaySelection } from 'lib/hooks/use-pay-selection';
import { useProductTexts } from 'lib/hooks/use-product-texts';
import type { ReservationRemovals } from 'lib/hooks/use-reservation-removals';

/** Item-breakdown row, optionally led by the protected icon when order protection covers it. */
function ItemLine({
  icon,
  label,
  amount
}: {
  icon?: boolean;
  label: React.ReactNode;
  amount: number;
}) {
  return <Stack direction="row" spacing={1} sx={{
    alignItems: 'center',
    justifyContent: 'space-between'
  }}>
      <Stack direction="row" spacing={1} sx={{
      alignItems: 'center',
      minWidth: 0
    }}>
        {icon && <ProtectedIcon sx={{
        fontSize: 16,
        flexShrink: 0
      }} />}
        <Typography variant="body2">{label}</Typography>
      </Stack>
      <Typography variant="body2" sx={{
      flexShrink: 0
    }}>
        <FormattedPrice amount={amount} />
      </Typography>
    </Stack>;
}
export const CartOrderSummary = ({
  hideItemsBreakdown,
  plain,
  order: orderProp,
  exchange,
  orderProtection,
  selection,
  removals,
  showDiscountCode,
  ...rest
}: {
  /**
   * Drops the title and the tinted rounded container so the totals sit directly
   * on the surface behind them. Used where the parent already supplies the
   * background, such as the mini cart's footer.
   */
  plain?: boolean;
  hideItemsBreakdown?: boolean;
  /** Shows the discount code row. Only offered on the main cart page, where a
   *  code can still be applied before checkout. */
  showDiscountCode?: boolean;
  /** Order to summarise. Defaults to the order held in app state. */
  order?: OrderResponse;
  /** Exchange cart. While a swap is in progress the summary is priced by it
   *  instead of by the order: the returned tickets are credited as their own
   *  line, the change fee is added, and the total is what is owed or refunded. */
  exchange?: ExchangeCart;
  /** Order protection offer, when the checkout step is offering one. Accepting
   *  it protects the order, so it adds its own priced line and marks the
   *  tickets line as protected. */
  orderProtection?: {
    accepted: boolean;
    price: number;
  };
  /** Select-items-to-pay. While selecting, the summary prices only what the
   *  buyer has ticked and says so, the rest being kept as a new reservation. */
  selection?: PaySelection;
  /** Soft removals on a reservation. What has been removed is priced out of the
   *  summary while the reservation still holds it. */
  removals?: ReservationRemovals;
}) => {
  const appState = useAppState();
  const intl = useIntl();
  const productTexts = useProductTexts();
  const isSelecting = !!selection?.isSelecting;
  const order = isSelecting ? selection?.selectedOrder : removals?.isActive ? removals.keptOrder : orderProp ?? appState.eventData.order;
  const activeExchange = exchange?.isExchange ? exchange : undefined;
  // Once order protection is accepted, the order carries it as its own
  // Insurance service line — that's the only signal once we're past the
  // checkout step's own offer/accept state, so it's the fallback whenever the
  // caller doesn't pass an explicit orderProtection prop (e.g. the mini cart).
  const protectionService = order?.services?.find(service => service.type === 'Insurance');
  const protectionAccepted = orderProtection ? orderProtection.accepted : !!protectionService;
  const protectionPrice = orderProtection ? orderProtection.price : protectionService?.price ?? 0;
  const subtotalBreakdown = [];

  // An exchange's tax is the summary's own line, and an ordinary order's is the
  // order's. Either way the figure is the API's to state, never ours to derive.
  const orderTax = activeExchange ? activeExchange.salesTax : order?.taxAmount ?? 0;
  subtotalBreakdown.push({
    label: <FormattedMessage id="common.salesTax" defaultMessage={[{
      "type": 0,
      "value": "Sales tax"
    }]} />,
    amount: orderTax
  });
  const orderSeparateFees = orderTotalTicketSeparateFees(order);
  if (orderSeparateFees > 0) {
    subtotalBreakdown.push({
      label: <FormattedMessage id="common.ticketFees" defaultMessage={[{
        "type": 0,
        "value": "Ticket fees"
      }]} />,
      amount: orderSeparateFees
    });
  }

  // Every charge the order carries gets its own line and comes back out of the
  // subtotal below, so the lines add up to the total whatever the order holds. An
  // exchange is priced by its own summary instead and keeps the two it showed.
  const chargeGroups = getOrderChargeGroups(order, activeExchange ? ['Delivery', 'TransactionFee'] : undefined);
  chargeGroups.forEach(({
    service,
    count,
    price
  }) => subtotalBreakdown.push({
    label: getOrderServiceLabel(service, count),
    amount: price
  }));
  order?.appliedGiftCards?.forEach(giftcardPayment => subtotalBreakdown.push(giftcardPayment.amountUsed && giftcardPayment.amountUsed > 0 ? {
    label: `${giftcardPayment.name} #${giftcardPayment.number}`,
    amount: -(giftcardPayment.amountUsed ?? 0)
  } : null));
  if (activeExchange && activeExchange.changeFee > 0) {
    subtotalBreakdown.push({
      label: <FormattedMessage id="exchangeCart.changeFee" defaultMessage={[{
        "type": 0,
        "value": "Ticket exchange fee"
      }]} />,
      amount: activeExchange.changeFee
    });
  }

  // Nets to a credit when the new tickets carry lower additive fees than the
  // returned ones, so a non-zero test rather than a positive one keeps the
  // lines adding up to the total.
  if (activeExchange && activeExchange.serviceCharge !== 0) {
    subtotalBreakdown.push({
      label: <FormattedMessage id="common.serviceCharge" defaultMessage={[{
        "type": 0,
        "value": "Service charge"
      }]} />,
      amount: activeExchange.serviceCharge
    });
  }
  const subtotal = activeExchange ? activeExchange.subtotal : order?.subtotal ?? 0;
  const total = activeExchange ? activeExchange.total : order?.remainingAmount ?? 0;
  return <Stack sx={{
    gap: 2,
    width: {
      xs: '100%',
      md: '464px'
    },
    maxWidth: '100%'
  }} {...rest}>
      <Box sx={theme => ({
      display: 'flex',
      justifyContent: 'center',
      flexDirection: 'column',
      gap: 2,
      ...(plain ? {} : {
        backgroundColor: theme.palette.containerBackground.main,
        p: 3,
        borderRadius: `${theme.containerRadius.main}px`
      })
    })}>
        {!plain && <Typography variant="h6" variantMapping={{
        h6: 'h2'
      }} sx={{
        textAlign: 'center'
      }}>
            {isSelecting ? <FormattedMessage id="cart.selectedToPay" defaultMessage={[{
          "type": 0,
          "value": "Selected to pay"
        }]} /> : <FormattedMessage id="cart.orderSummary" defaultMessage={[{
          "type": 0,
          "value": "Order summary"
        }]} />}
          </Typography>}
        {!plain && isSelecting && <Typography variant="body2" sx={{
        textAlign: 'center',
        color: 'text.secondary'
      }}>
            <FormattedMessage id="cart.selectionHelper" defaultMessage={[{
          "type": 0,
          "value": "You will only pay for the selected items."
        }, {
          "type": 8,
          "value": "br",
          "children": []
        }, {
          "type": 0,
          "value": "The remaining items will be saved in a new reservation."
        }]} />
          </Typography>}
        {(!hideItemsBreakdown || protectionAccepted) && <>
            <Stack spacing={0.25}>
              {!hideItemsBreakdown && <>
                  {activeExchange && activeExchange.returnedTickets.length > 0 && <ItemLine label={<FormattedMessage id="exchangeCart.xTimesReturnedTickets" defaultMessage={[{
              "type": 6,
              "value": "count",
              "options": {
                "one": {
                  "value": [{
                    "type": 7
                  }, {
                    "type": 0,
                    "value": " x returned ticket"
                  }]
                },
                "other": {
                  "value": [{
                    "type": 7
                  }, {
                    "type": 0,
                    "value": " x returned tickets"
                  }]
                }
              },
              "offset": 0,
              "pluralType": "cardinal"
            }]} values={{
              count: activeExchange.returnedTickets.length
            }} />} amount={-activeExchange.returnedTicketsTotal} />}
                  {getOrderTicketsCount(order) > 0 && <ItemLine icon={protectionAccepted} label={<FormattedMessage id="common.xTimesTickets" defaultMessage={[{
              "type": 6,
              "value": "count",
              "options": {
                "one": {
                  "value": [{
                    "type": 7
                  }, {
                    "type": 0,
                    "value": " x ticket"
                  }]
                },
                "other": {
                  "value": [{
                    "type": 7
                  }, {
                    "type": 0,
                    "value": " x tickets"
                  }]
                }
              },
              "offset": 0,
              "pluralType": "cardinal"
            }]} values={{
              count: getOrderTicketsCount(order)
            }} />}
            // What the staged tickets are billed at, which is the
            // returned tickets' value on a price-matched swap. The
            // staging order holds the cheaper selection instead.
            amount={activeExchange ? activeExchange.newTicketsTotal : getOrderTicketsTotal(order)} />}
                  {getOrderMembershipsCount(order) > 0 && <ItemLine label={<FormattedMessage id="common.xTimesMemberships" defaultMessage={[{
              "type": 6,
              "value": "count",
              "options": {
                "one": {
                  "value": [{
                    "type": 7
                  }, {
                    "type": 0,
                    "value": " x membership"
                  }]
                },
                "other": {
                  "value": [{
                    "type": 7
                  }, {
                    "type": 0,
                    "value": " x memberships"
                  }]
                }
              },
              "offset": 0,
              "pluralType": "cardinal"
            }]} values={{
              count: getOrderMembershipsCount(order)
            }} />} amount={getOrderMembershipsTotal(order)} />}
                  {getOrderGiftCardsCount(order) > 0 && <ItemLine label={<FormattedMessage id="cart.xTimesGiftcards" defaultMessage={[{
              "type": 6,
              "value": "count",
              "options": {
                "one": {
                  "value": [{
                    "type": 7
                  }, {
                    "type": 0,
                    "value": " x gift card"
                  }]
                },
                "other": {
                  "value": [{
                    "type": 7
                  }, {
                    "type": 0,
                    "value": " x gift cards"
                  }]
                }
              },
              "offset": 0,
              "pluralType": "cardinal"
            }]} values={{
              count: getOrderGiftCardsCount(order)
            }} />} amount={getOrderGiftCardsTotal(order)} />}
                  {getOrderDonationsCount(order) > 0 && <ItemLine label={<FormattedMessage id="cart.xTimesDonations" defaultMessage={[{
              "type": 6,
              "value": "count",
              "options": {
                "one": {
                  "value": [{
                    "type": 7
                  }, {
                    "type": 0,
                    "value": " x donation"
                  }]
                },
                "other": {
                  "value": [{
                    "type": 7
                  }, {
                    "type": 0,
                    "value": " x donations"
                  }]
                }
              },
              "offset": 0,
              "pluralType": "cardinal"
            }]} values={{
              count: getOrderDonationsCount(order)
            }} />} amount={getOrderDonationsTotal(order)} />}
                  {getOrderProductsCount(order) > 0 && <ItemLine label={<FormattedMessage id="common.xTimesCharge" defaultMessage={[{
              "type": 1,
              "value": "count"
            }, {
              "type": 0,
              "value": " x "
            }, {
              "type": 1,
              "value": "label"
            }]} values={{
              count: getOrderProductsCount(order),
              label: productTexts.cartTitle.toLocaleLowerCase(intl.locale)
            }} />} amount={getOrderProductsTotal(order)} />}
                </>}
              {protectionAccepted && <ItemLine icon label={<FormattedMessage id="checkout.orderProtection.summaryLine" defaultMessage={[{
            "type": 0,
            "value": "Order protection"
          }]} />} amount={protectionPrice} />}
            </Stack>
            <Divider />
          </>}
        <Box>
          <Typography variant="body2" sx={{
          fontWeight: 700,
          display: 'flex',
          justifyContent: 'space-between'
        }}>
            <FormattedMessage id="common.subtotal" defaultMessage={[{
            "type": 0,
            "value": "Subtotal"
          }]} />
            <span>
              <FormattedPrice amount={subtotal} />
            </span>
          </Typography>
          {subtotalBreakdown.map(({
          label,
          amount
        }, index) => <Typography key={index} variant="body2" sx={{
          lineHeight: 1.75,
          color: 'text.secondary',
          display: 'flex',
          justifyContent: 'space-between'
        }}>
              {label}
              <span>
                <FormattedPrice amount={amount} />
              </span>
            </Typography>)}
        </Box>
        {showDiscountCode && <CartDiscountCode order={order} />}
        <Divider />
        <Typography variant="body1Medium" sx={{
        fontWeight: 700,
        display: 'flex',
        justifyContent: 'space-between'
      }}>
          {/* An exchange always reads as a total, the sign carrying whether
              money comes back. Only an ordinary cart relabels a credit. */}
          {!activeExchange && total < 0 ? <FormattedMessage id="common.balance" defaultMessage={[{
          "type": 0,
          "value": "Balance"
        }]} /> : <FormattedMessage id="common.total" defaultMessage={[{
          "type": 0,
          "value": "Total"
        }]} />}
          <span>
            <FormattedPrice amount={total} />
          </span>
        </Typography>
      </Box>
    </Stack>;
};